import {
  DISCHARGE_DISPOSITION_CHOICES,
  NPHIES_INTENDED_STAY_CODES,
  defaultDischargeDisposition,
  isDischargeDisposition,
  type NphiesDischargeDisposition,
  type NphiesIntendedStayCode,
} from '@his/shared';
import type { ChoiceOption } from '@/components/ui/choice-buttons';
import { tr } from '@/lib/i18n-core';

export interface SpecialtyOption {
  id: string;
  name: string;
  nphiesPracticeCode: string | null;
  isActive: boolean;
}

export interface CodedCause {
  code: string;
  title: string;
}

export function intendedStayChoices(): ChoiceOption<NphiesIntendedStayCode>[] {
  return NPHIES_INTENDED_STAY_CODES.map((value) => ({ value, label: tr('ipd', `intended_stay_${value}`) }));
}

export function dispositionLabel(code: string): string {
  return tr('ipd', `discharge_disposition_${code}`);
}

function fitsStatus(status: string, disposition: NphiesDischargeDisposition): boolean {
  return (disposition === 'died') === (status === 'expired');
}

export function dispositionChoices(status: string): ChoiceOption<NphiesDischargeDisposition>[] {
  return DISCHARGE_DISPOSITION_CHOICES.map((value) => ({
    value,
    label: dispositionLabel(value),
    disabled: !fitsStatus(status, value),
    ...(value === 'died' ? { tone: 'destructive' as const } : {}),
  }));
}

export function dispositionFor(status: string, chosen: string | null | undefined): NphiesDischargeDisposition | null {
  return isDischargeDisposition(chosen) && fitsStatus(status, chosen) ? chosen : defaultDischargeDisposition(status);
}

export function selectableSpecialties(list: readonly SpecialtyOption[], keepId: string | null | undefined): SpecialtyOption[] {
  return list.filter((specialty) => specialty.isActive || specialty.id === keepId);
}

export function specialtyOptionLabel(specialty: SpecialtyOption): string {
  const code = specialty.nphiesPracticeCode?.trim();
  return code ? `${specialty.name} · ${code}` : tr('ipd', 'specialty_no_code', { name: specialty.name });
}
